/* eslint-disable array-callback-return */
// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import updatePdfSettings from "@adl/App/Handlers/CustomQuote/UpdatePdfSettings";
import { CustomQuoteModel, loadCustomQuote, LoadQuoteParams } from "@adl/App/Services/CustomQuotesService";
import {
    ApiHandler,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
} from "@insite/client-framework/HandlerCreator";
import { GetProductCollectionRealTimePriceApiV2Parameter } from "@insite/client-framework/Services/ProductServiceV2";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";

type HandlerType = ApiHandler<LoadQuoteParams & HasOnSuccess<CustomQuoteModel> & HasOnError<string>, CustomQuoteModel>;

export const BeginLoadingquotes: HandlerType = props => {
    props.dispatch({
        type: "PdfSettings/ClearSettings",
    }as any);
    props.dispatch({
        type: "Pages/xMyCustomQuotes/BeginLoadingQuoteDetails",
        sortBy: props.parameter.sortBy
    } as any);
};

export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = {
        ...props.parameter,
    };
};

export const CallApiWithParameters: HandlerType = async props => {
    try {
        props.apiResult = await loadCustomQuote(props.apiParameter);
    } catch (error) {
        if ("status" in error && (error.status === 400 || error.status === 404)) {
            props.dispatch({
                type: "Data/Quotes/FailedToLoadQuote",
                quoteId: props.parameter.quoteId,
                status: 404,
            });
            return false;
        }
        throw error;
    }
};

export const LoadRealTimePrices: HandlerType = async props => {
    if (props.parameter.skipPricingAndInventory) {
        return;
    }
    const loadPriceParamter: GetProductCollectionRealTimePriceApiV2Parameter = { productPriceParameters: [] };
    props.apiResult.linesModel?.map((line: { productResult: { id: any }; unitOfMeasureDisplay: any; qty: any }) =>
        loadPriceParamter.productPriceParameters.push({
            productId: line.productResult.id,
            unitOfMeasure: line.unitOfMeasureDisplay,
            qtyOrdered: line.qty,
        }),
    );

    const quoteLines = props.apiResult.linesModel;
    if (quoteLines) {
        props.dispatch(
            await loadRealTimePricing({
                productPriceParameters: loadPriceParamter.productPriceParameters,
                onSuccess(result) {
                    props.dispatch({
                        type: "Pages/xMyCustomQuotes/CompleteLoadingPricing",
                        realPricingInfo: result.realTimePricingResults,
                        realTimePricingFailed: false,
                    } as any);
                },
                onError(result) {
                    props.dispatch({
                        type: "Pages/xMyCustomQuotes/CompleteLoadingPricing",
                        realTimePricingFailed: true,
                    } as any);
                },
            }),
        );
    }

    props.apiResult.linesModel = quoteLines;
};

export const LoadRealTimeInventory: HandlerType = async props => {
    if (props.parameter.skipPricingAndInventory) {
        return;
    }
    const quoteLines = props.apiResult.linesModel;
    if (quoteLines) {
        props.dispatch(
            await loadRealTimeInventory({
                productIds: props.apiResult.linesModel.map(
                    (line: { productResult: { id: any } }) => line.productResult.id,
                ),
                additionalQueryStringParameters: {isCustomQuote: true},
                onSuccess(result) {
                    props.dispatch({
                        type: "Pages/xMyCustomQuotes/CompleteLoadingInventory",
                        realInventoryInfo: result.realTimeInventoryResults,
                        realTimeInventoryFailed: false,
                    } as any);
                },
                onError(result) {
                    props.dispatch({
                        type: "Pages/xMyCustomQuotes/CompleteLoadingInventory",
                        realTimeInventoryFailed: true,
                    } as any);
                },
            }),
        );
    }
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    const quote = props.apiResult;
    props.dispatch(
        updatePdfSettings({
            parameters: {
                website: quote.website,
                clientName: quote.clientName,
                phoneNumber: quote.phoneNumber,
                streetAddress1: quote.streetAddress1,
                streetAddress2: quote.streetAddress2,
                email: quote.email,
                city: quote.city,
                province: quote.province,
                postalCode: quote.postalCode,
                freightCharges: quote.freightCharges,
                shopSupplies: quote.shopSupplies,
                labourCharges: quote.labourCharges,
                priceDisplayOption: quote.priceDisplayType,
                expirationDate: quote.expiryDate,
                logoFileName: quote.logoFileName,
                id: quote.id,
                generalNote: quote.generalNote}
        }),
    );
    props.dispatch({
        type: "Pages/xMyCustomQuotes/CompleteLoadingQuoteDetails",
        quote: props.apiResult,
        skipPricingAndInventory: props.parameter.skipPricingAndInventory,
    } as any);
};

export const chain = [
    BeginLoadingquotes,
    PopulateApiParameter,
    CallApiWithParameters,
    LoadRealTimePrices,
    LoadRealTimeInventory,
    ExecuteOnSuccessCallback,
];

const getQuoteDetails = createHandlerChainRunner(chain, "GetQuoteDetails");
export default getQuoteDetails;
